import React, { useState, useMemo } from 'react';
import { useTheme } from '../../styles/ThemeProvider';
import { SkeletonTable, EmptyState } from './states';

export const DataTable = ({ columns, rows, onRowClick, loading, emptyLabel, initialSort }) => {
  const { theme } = useTheme();
  const [sort, setSort] = useState(initialSort || null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), 'tr');
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort, columns]);

  if (loading) return <SkeletonTable />;
  if (!rows || rows.length === 0) return <EmptyState title={emptyLabel || 'Veri yok'} />;

  const toggleSort = (key) => {
    if (!sort || sort.key !== key) return setSort({ key, dir: 'desc' });
    if (sort.dir === 'desc') return setSort({ key, dir: 'asc' });
    return setSort(null);
  };

  return (
    <div style={{ overflow: 'auto', border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md, fontFamily: theme.font.family }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: theme.font.size.sm }}>
        <thead style={{ background: theme.color.surfaceAlt, position: 'sticky', top: 0 }}>
          <tr>
            {columns.map(c => (
              <th key={c.key}
                onClick={c.sortable !== false ? () => toggleSort(c.key) : undefined}
                style={{
                  textAlign: c.align || 'left',
                  padding: `${theme.space.md}px ${theme.space.base}px`,
                  fontWeight: theme.font.weight.semibold,
                  color: theme.color.textSoft,
                  fontSize: theme.font.size.xs,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  cursor: c.sortable !== false ? 'pointer' : 'default',
                  userSelect: 'none',
                  borderBottom: `1px solid ${theme.color.border}`,
                  whiteSpace: 'nowrap',
                }}>
                {c.label}
                {sort?.key === c.key && <span style={{ marginLeft: 4 }}>{sort.dir === 'asc' ? '↑' : '↓'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r, i) => (
            <tr key={r.id ?? i}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              style={{
                cursor: onRowClick ? 'pointer' : 'default',
                background: i % 2 === 0 ? theme.color.surface : theme.color.surfaceAlt,
                transition: 'background 0.1s',
              }}
              onMouseEnter={onRowClick ? (e) => e.currentTarget.style.background = theme.color.border : undefined}
              onMouseLeave={onRowClick ? (e) => e.currentTarget.style.background = i % 2 === 0 ? theme.color.surface : theme.color.surfaceAlt : undefined}>
              {columns.map(c => (
                <td key={c.key} style={{
                  padding: `${theme.space.md}px ${theme.space.base}px`,
                  textAlign: c.align || 'left',
                  color: theme.color.text,
                  borderBottom: `1px solid ${theme.color.border}`,
                  whiteSpace: c.nowrap ? 'nowrap' : 'normal',
                }}>
                  {c.render ? c.render(r[c.key], r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
